import { useEffect, useRef } from "react";
import { Bold, Italic, List, ListOrdered, Link2, Eraser } from "lucide-react";

const BTN = "p-1.5 rounded-md text-ink-500 hover:text-navy hover:bg-linen transition-colors";

export const RichTextEditor = ({ value, onChange, placeholder = "", minHeight = 150, testid = "rich-text" }) => {
  const ref = useRef(null);
  const last = useRef(value || "");

  useEffect(() => {
    if (ref.current && (value || "") !== last.current) {
      ref.current.innerHTML = value || "";
      last.current = value || "";
    }
  }, [value]);

  const emit = () => {
    const html = ref.current?.innerHTML || "";
    last.current = html;
    onChange(html === "<br>" || html === "<p><br></p>" ? "" : html);
  };

  const cmd = (command, arg) => {
    if (document.activeElement !== ref.current) ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Link URL (include https://):", "https://");
    if (!url || !/^(https?:\/\/|mailto:|tel:)/i.test(url)) return;
    cmd("createLink", url);
  };

  const onPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
    emit();
  };

  return (
    <div className="mt-1.5 rounded-xl border border-linen-border bg-white overflow-hidden focus-within:ring-2 focus-within:ring-teal/40">
      <div className="flex items-center gap-0.5 border-b border-linen-border bg-linen/60 px-2 py-1.5">
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("bold")} data-testid={`${testid}-bold`} title="Bold" className={BTN}><Bold size={14} /></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("italic")} data-testid={`${testid}-italic`} title="Italic" className={BTN}><Italic size={14} /></button>
        <span className="mx-1 h-4 w-px bg-linen-border" />
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("insertUnorderedList")} data-testid={`${testid}-ul`} title="Bullet list" className={BTN}><List size={14} /></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("insertOrderedList")} data-testid={`${testid}-ol`} title="Numbered list" className={BTN}><ListOrdered size={14} /></button>
        <span className="mx-1 h-4 w-px bg-linen-border" />
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={addLink} data-testid={`${testid}-link`} title="Add link" className={BTN}><Link2 size={14} /></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("removeFormat")} data-testid={`${testid}-clear`} title="Clear formatting" className={BTN}><Eraser size={14} /></button>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-ink-400 pr-1">Rich text</span>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        data-placeholder={placeholder}
        data-testid={testid}
        style={{ minHeight }}
        className="grc-rte px-3.5 py-3 text-sm text-navy leading-relaxed outline-none"
      />
    </div>
  );
};
